import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../i18n/LocaleContext';
import {
  ArrowRight,
  Eye,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';

export default function Login() {
  const shellRef = useRef(null);
  const contentRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileLayout, setMobileLayout] = useState({ scale: 1, height: null });
  const { login, verify2fa } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const legalLocale = locale === 'cs' ? 'cs' : 'en';

  useEffect(() => {
    const updateMobileLayout = () => {
      if (!shellRef.current || !contentRef.current || window.innerWidth >= 640) {
        setMobileLayout((current) => (
          current.scale === 1 && current.height === null
            ? current
            : { scale: 1, height: null }
        ));
        return;
      }

      const availableHeight = Math.max(shellRef.current.clientHeight - 24, 0);
      const contentHeight = contentRef.current.scrollHeight;
      const rawScale = contentHeight > availableHeight ? availableHeight / contentHeight : 1;
      const scale = Math.max(0.78, Math.min(1, rawScale));
      const nextLayout = {
        scale: Number(scale.toFixed(3)),
        height: Math.round(contentHeight * scale),
      };

      setMobileLayout((current) => (
        current.scale === nextLayout.scale && current.height === nextLayout.height
          ? current
          : nextLayout
      ));
    };

    const frame = window.requestAnimationFrame(updateMobileLayout);
    window.addEventListener('resize', updateMobileLayout);
    window.visualViewport?.addEventListener('resize', updateMobileLayout);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateMobileLayout);
      window.visualViewport?.removeEventListener('resize', updateMobileLayout);
    };
  }, [challengeToken, error]);

  const isScaledMobile = mobileLayout.scale < 0.999;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = challengeToken
        ? await verify2fa(challengeToken, twoFactorCode)
        : await login(email, password);

      if (user?.requires_2fa) {
        setChallengeToken(user.challenge_token);
        setTwoFactorCode('');
        setLoading(false);
        return;
      }

      navigate(user.role === 'admin' ? '/admin' : '/app');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-[#F6F7F9] xl:grid xl:h-[100dvh] xl:grid-cols-[minmax(0,0.44fr)_minmax(0,0.56fr)] xl:overflow-hidden">
      <div ref={shellRef} className="relative flex min-h-[100dvh] items-center justify-center px-4 py-4 sm:px-8 sm:py-8 md:px-10 lg:px-12 xl:min-h-0 xl:overflow-y-auto">
        <div className="flex w-full justify-center">
          <div
            className="relative w-full max-w-[560px]"
            style={isScaledMobile ? { height: `${mobileLayout.height}px` } : undefined}
          >
            <div
              ref={contentRef}
              className="mx-auto w-full max-w-[560px]"
              style={isScaledMobile ? {
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: `translateX(-50%) scale(${mobileLayout.scale})`,
                transformOrigin: 'top center',
              } : undefined}
            >
              <div className="relative rounded-[26px] border border-[#E7EAF0] bg-white px-6 py-7 shadow-[0_24px_60px_-34px_rgba(14,23,38,0.18)] sm:px-8 sm:py-9">
            <img
              src="/brand/linirio-horizontal-color-light-removebg-preview.png"
              alt="Linirio"
              className="absolute right-6 top-6 h-auto w-[112px] object-contain sm:right-8 sm:top-8 sm:w-[142px]"
            />
            <p className="pr-[124px] text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7485] sm:pr-[158px]">Operator access</p>
            <h1 className="mt-4 text-[2rem] font-display leading-[1.05] text-[#0E1726] sm:text-[2.25rem]">Welcome back</h1>
            <p className="mt-3 text-[15px] leading-7 text-[#6B7485]">
              {challengeToken
                ? 'Enter your authenticator code to finish the secure session.'
                : 'Sign in to continue to your workspace.'}
            </p>

            {error && (
              <div className="mt-6 rounded-[18px] border border-[#F2B8B5] bg-[#FFF3F2] px-4 py-3 text-sm text-[#B42318]">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              {!challengeToken && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#0E1726]">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="brand-input w-full rounded-[18px] border-[#E7EAF0] px-4 py-3.5"
                    placeholder="you@company.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              )}

              {!challengeToken && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#0E1726]">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="brand-input w-full rounded-[18px] border-[#E7EAF0] px-4 py-3.5"
                    placeholder="********"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              )}

              {challengeToken && (
                <div>
                  <label className="block text-sm font-medium text-[#0B1D3A] mb-2">
                    2FA code
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    autoComplete="one-time-code"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase().slice(0, 32))}
                    className="brand-input w-full rounded-[18px] border-[#E7EAF0] px-4 py-3.5 tracking-[0.25em]"
                    placeholder="000000"
                    required
                    data-testid="login-2fa-input"
                  />
                  <button
                    type="button"
                    onClick={() => { setChallengeToken(''); setTwoFactorCode(''); setError(''); }}
                    className="mt-3 text-sm text-[#6B7485] transition hover:text-[#0E1726]"
                  >
                    Back to password
                  </button>
                </div>
              )}

              {!challengeToken && (
                <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <label className="inline-flex items-center gap-3 text-[#6B7485]">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-[#C8D0DC] text-[#11C8D8] focus:ring-[#11C8D8]"
                    />
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setError('Password reset is handled by your workspace administrator.')}
                    className="text-left font-medium text-[#0E1726] transition hover:text-[#11C8D8] sm:text-right"
                  >
                    Forgot password
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-[18px] bg-gradient-to-r from-[#49E93A] via-[#18D7C8] to-[#11C8D8] px-4 py-3.5 font-medium text-white shadow-[0_16px_30px_-18px_rgba(17,200,216,0.55)] transition-all hover:translate-y-[-1px] hover:shadow-[0_18px_34px_-18px_rgba(17,200,216,0.62)] disabled:translate-y-0 disabled:opacity-60"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {challengeToken ? 'Verify code' : 'Sign in'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-6 text-sm leading-6 text-[#6B7485]">
              By signing in, you agree to our{' '}
              <a href={`https://linirio.com/${legalLocale}/terms`} className="font-medium text-[#0E1726] hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={`https://linirio.com/${legalLocale}/privacy`} className="font-medium text-[#0E1726] hover:underline">
                Privacy Policy
              </a>
              .
            </p>

            <p className="mt-6 text-center text-sm text-[#6B7485]">
              Need an account?{' '}
              <Link to="/register" className="text-[#0B1D3A] font-medium hover:underline">
                Create one
              </Link>
            </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden overflow-hidden bg-[#03111D] px-5 py-10 text-[#F7FBFF] md:block md:px-10 lg:px-12 xl:flex xl:min-h-0 xl:items-center xl:overflow-y-auto">
        <div className="pointer-events-none absolute inset-0 opacity-70">
          <div className="absolute inset-0 [background-image:linear-gradient(rgba(185,200,216,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(185,200,216,0.08)_1px,transparent_1px)] [background-size:34px_34px]" />
          <div className="absolute -right-24 top-10 h-64 w-64 rounded-full bg-[#63F20A]/10 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-[#11C8D8]/10 blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-[560px]">
          <div className="inline-flex items-center rounded-full border border-white/12 bg-white/6 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#D9F8EE]">
            NIS2-ready controls
          </div>

          <h2 className="mt-6 max-w-[14ch] text-[2rem] font-display leading-[1.08] text-[#F7FBFF] sm:text-[2.35rem] lg:text-[2.65rem]">
            Secure AI workflow for <span className="bg-gradient-to-r from-[#63F20A] to-[#18D7C8] bg-clip-text text-transparent">conversations</span>, <span className="bg-gradient-to-r from-[#49E93A] to-[#11C8D8] bg-clip-text text-transparent">approvals</span> and follow-ups.
          </h2>

          <p className="mt-5 max-w-[560px] text-[15px] leading-7 text-[#B9C8D8] sm:text-base">
            Linirio orchestrates AI interactions with built-in guardrails, approvals, and auditability, so your team gets the speed of AI with the control you need.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <TrustCard icon={Eye} label="Audit visibility" />
            <TrustCard icon={LockKeyhole} label="Secure sessions" />
            <TrustCard icon={UserCheck} label="Human approval" />
            <TrustCard icon={ShieldCheck} label="Autonomous safeguards" />
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustCard({ icon: Icon, label }) {
  return (
    <div className="rounded-[22px] border border-white/12 bg-white/[0.045] px-4 py-4 backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.055] text-[#9AF48E]">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div className="text-sm font-medium text-[#F7FBFF]">{label}</div>
      </div>
    </div>
  );
}
